import {AngularComponentService} from '../mx-graph/graph/angular-component.service';

declare const mxGeometry: any;
declare const mxPoint: any;
declare const mxUtils: any;

import {Injectable} from '@angular/core';
import {Q9HtmlEncoderPipe} from '@q9elements/ui-kit/common';
import {EditorRegistryService} from '../mx-graph/editor/editor-registry.service';
import {ShapesTemplateService} from './shapes-template.service';
import {GraphHelperService} from '../helpers/graph-helper.service';
import {StylesService} from '../styles/styles.service';
import {setShapeValue} from '../helpers/set-shape.util';
import {
  IActJson,
  IFlowlineJson,
  IImageJson,
  IInteractionJson,
  ILineTextJson,
  IStickerJson,
  ITextJson
} from '../../diagram-data/diagram-json-handler.service';
import {CircularDepCrutch} from '../../core/helpers/circular-dep.crutch';
import {StickersService} from '../../diagram-interaction/services/stickers.service';
import {AuthService} from 'src/app/core/auth/auth.service';
import {environment} from 'src/environments/environment';
import {DiagramTypeConfigService} from '../../core/services/diagram-type-config.service';

@Injectable()
export class ShapeSpawnerService extends CircularDepCrutch {
  private isArchitecture: boolean;
  // stickers service injected separately to avoid cyclic dependency
  public stickersSvc: StickersService;
  constructor(
    private editorRegistrySvc: EditorRegistryService,
    private templateSvc: ShapesTemplateService,
    private graphHelper: GraphHelperService,
    private stylesSvc: StylesService,
    private angularCompSvc: AngularComponentService,
    private authService: AuthService,
    private diagramTypeConfigSvc: DiagramTypeConfigService,
    private readonly encodeHtml: Q9HtmlEncoderPipe
  ) {
    super();
  }

  public setArchitecture(val: boolean) {
    this.isArchitecture = val;
  }

  public createConnector(data, flMxKey, parent?) {
    const graph = this.editorRegistrySvc.getGraph();
    const styles = this.stylesSvc.connector(data);
    const insertedVertex = graph.createVertex(
      parent || this.graphHelper.connectorAndTerminatorParent(),
      data.mxKey,
      null,
      data.position.x,
      data.position.y,
      data.position.w,
      data.position.h,
      styles.mxCellCss()
    );

    insertedVertex.setConnectable(false);

    if (!data.flowlines) {
      data.flowlines = [];
    }

    this.angularCompSvc.initConnectorComponent(insertedVertex, flMxKey);

    return insertedVertex;
  }

  public createTerminator(data, parent?) {
    const graph = this.editorRegistrySvc.getGraph();
    const styles = this.stylesSvc.terminator(data);
    const insertedVertex = graph.createVertex(
      parent || this.graphHelper.connectorAndTerminatorParent(),
      data.mxKey,
      'X',
      data.position.x,
      data.position.y,
      data.position.w,
      data.position.h,
      styles.mxCellCss()
    );

    insertedVertex.setConnectable(false);

    if (!data.flowlines) {
      data.flowlines = [];
    }

    return insertedVertex;
  }

  public createText(data: ITextJson, parent?) {
    const graph = this.editorRegistrySvc.getGraph();
    const template = this.templateSvc.text(data);
    const styles = this.stylesSvc.text(data);
    const insertedVertex = graph.createVertex(
      parent || this.graphHelper.textParent(),
      data.mxKey,
      template,
      data.position.x,
      data.position.y,
      data.position.w,
      data.position.h,
      styles.mxCellCss()
    );

    insertedVertex.setConnectable(false);

    return insertedVertex;
  }

  public createActivity(data: IActJson, parent?) {
    const graph = this.editorRegistrySvc.getGraph();
    const template = this.templateSvc.activity(data);
    const styles = this.stylesSvc.activity(data);
    const insertedVertex = graph.createVertex(
      parent || this.graphHelper.activityParent(),
      data.mxKey,
      template,
      data.position.x,
      data.position.y,
      data.position.w,
      data.position.h,
      styles.mxCellCss({isArchitecture: this.isArchitecture})
    );

    insertedVertex.setConnectable(true);
    this.angularCompSvc.initActivityComponent(insertedVertex, data);
    this.angularCompSvc.refreshLabelStyles(insertedVertex);

    return insertedVertex;
  }

  public createFlowline(
    data: IFlowlineJson,
    useUnscaled?: boolean,
    source?: any,
    target?: any,
    parent?
  ) {
    const graph = this.editorRegistrySvc.getGraph();
    const template = this.templateSvc.flowline(data);
    const styles = this.stylesSvc.flowline(data);

    const flMxKey = data.mxKey || null;

    const insertedEdge = graph.createEdge(
      parent || this.graphHelper.flowlineParent(),
      flMxKey,
      template,
      source,
      target,
      styles.mxCellCss()
    );

    const model = graph.getModel();

    model.beginUpdate();

    const geo = this.diagramTypeConfigSvc.flowlineSettings().geo(data);

    insertedEdge.setGeometry(geo);

    if (data.waypoints && data.waypoints.length > 0) {
      const scale = graph.view.scale;

      geo.points = data.waypoints.map(wp => {
        if (useUnscaled) {
          return new mxPoint(wp.x, wp.y);
        } else {
          return new mxPoint(wp.x / scale, wp.y / scale);
        }
      });
    }

    if (data.srcPosition) {
      const point = new mxPoint(data.srcPosition.x, data.srcPosition.y);

      geo.setTerminalPoint(point, true);
    }

    if (data.tgtPosition) {
      const point = new mxPoint(data.tgtPosition.x, data.tgtPosition.y);

      geo.setTerminalPoint(point, false);
    }

    if (source) {
      graph.model.setTerminal(insertedEdge, source, true);
      const style = insertedEdge.getStyle();

      insertedEdge.setStyle(mxUtils.setStyle(style, 'exitX', data.position.connectionPoints.exitX));
      insertedEdge.setStyle(mxUtils.setStyle(style, 'exitY', data.position.connectionPoints.exitY));
    }

    if (target) {
      graph.model.setTerminal(insertedEdge, target, false);
      const style = insertedEdge.getStyle();

      insertedEdge.setStyle(
        mxUtils.setStyle(style, 'entryX', data.position.connectionPoints.entryX)
      );
      insertedEdge.setStyle(
        mxUtils.setStyle(style, 'entryY', data.position.connectionPoints.entryY)
      );
    }

    if (data.lineTexts) {
      data.lineTexts.forEach(lineTextData => {
        const lineTextCell = this.createLineText(lineTextData, data);

        insertedEdge.insert(lineTextCell);
      });
    }

    model.endUpdate();

    return insertedEdge;
  }

  public createLineText(data: ILineTextJson, lineData: IFlowlineJson) {
    const graph = this.editorRegistrySvc.getGraph();
    const styles = this.stylesSvc.flowline(lineData);
    const {position, text, key} = data;
    const id = `${lineData.mxKey}-${key}`;
    const value = `<!--contentbegin-->${text}<!--contentend-->`;
    const vertex = graph.createVertex(
      null,
      id,
      value,
      position.x,
      position.y,
      0,
      0,
      styles.lineText(),
      true
    );

    vertex.geometry.offset = new mxPoint(position.offset.x, position.offset.y);
    vertex.setConnectable(false);
    vertex.key = data.key;

    return vertex;
  }

  // value is not a part of the data, because it is actually a index of this ia in array + 1
  public createInteractionCell(data: IInteractionJson, value, lineData: IFlowlineJson) {
    const graph = this.editorRegistrySvc.getGraph();
    const {offset} = data;
    const styles = this.stylesSvc.flowline(lineData);
    const id = `${data.flowlineMxKey}-ian`;
    const vertex = graph.createVertex(
      null,
      id,
      value,
      -0.75,
      0,
      22,
      22,
      styles.interaction(data),
      true
    );

    vertex.geometry.offset = new mxPoint(offset.x, offset.y);
    vertex.setConnectable(false);

    return vertex;
  }

  public updateFlowline(change: IFlowlineJson, edge, source?, target?) {
    const graph = this.editorRegistrySvc.getEditorUI().editor.graph;
    const model = graph.getModel();

    model.beginUpdate();

    const geo = this.diagramTypeConfigSvc.flowlineSettings().geo(change);

    if (change.srcPosition) {
      const point = new mxPoint(change.srcPosition.x, change.srcPosition.y);

      geo.setTerminalPoint(point, true);
      model.setTerminal(edge, null, true);
    }

    if (change.tgtPosition) {
      const point = new mxPoint(change.tgtPosition.x, change.tgtPosition.y);

      geo.setTerminalPoint(point, false);
      model.setTerminal(edge, null, false);
    }

    if (source) {
      edge.setTerminal(source, true);
      graph.setCellStyles('exitX', change.position.connectionPoints.exitX, [edge]);
      graph.setCellStyles('exitY', change.position.connectionPoints.exitY, [edge]);
    } else if (change.srcId) {
      const sourceCell = this.graphHelper
        .allActivities()
        .concat(this.graphHelper.allConnectorAndTerminators())
        .find(mxCell => Number(mxCell.id) === Number(change.srcId));

      model.setTerminal(edge, sourceCell, true);
      graph.setCellStyles('exitX', change.position.connectionPoints.exitX, [edge]);
      graph.setCellStyles('exitY', change.position.connectionPoints.exitY, [edge]);
    }

    if (target) {
      edge.setTerminal(target, false);
      graph.setCellStyles('entryX', change.position.connectionPoints.entryX, [edge]);
      graph.setCellStyles('entryY', change.position.connectionPoints.entryY, [edge]);
    } else if (change.tgtId) {
      const targetCell = this.graphHelper
        .allActivities()
        .concat(this.graphHelper.allConnectorAndTerminators())
        .find(mxCell => Number(mxCell.id) === Number(change.tgtId));

      model.setTerminal(edge, targetCell, false);
      graph.setCellStyles('entryX', change.position.connectionPoints.entryX, [edge]);
      graph.setCellStyles('entryY', change.position.connectionPoints.entryY, [edge]);
    }

    if (change.waypoints && change.waypoints.length > 0) {
      geo.points = change.waypoints.map(wp => new mxPoint(wp.x, wp.y));
    }

    const newCellValue = setShapeValue(edge, change.text);

    model.setValue(edge, newCellValue);
    model.setGeometry(edge, geo);
    model.endUpdate();
  }

  public createImage(data: IImageJson, url, parent?) {
    const graph = this.editorRegistrySvc.getGraph();
    const style = this.stylesSvc.image(data).mxCellCss({url});

    const insertedVertex = graph.createVertex(
      parent || this.graphHelper.imageParent(),
      data.mxKey,
      null,
      data.position.x,
      data.position.y,
      data.position.w,
      data.position.h,
      style
    );

    insertedVertex.setConnectable(false);

    return insertedVertex;
  }

  public createSticker(data: IStickerJson, parent?) {
    if (!data.tags) {
      data.tags = [];
    }

    const graph = this.editorRegistrySvc.getGraph();
    const template = this.templateSvc.sticker(data);
    const styles = this.stylesSvc.sticker(data);

    const insertedVertex = graph.createVertex(
      parent || this.graphHelper.stickerParent(),
      data.mxKey,
      template,
      data.position.x,
      data.position.y,
      data.position.w,
      data.position.h,
      styles.mxCellCss()
    );

    insertedVertex.setConnectable(false);

    if (environment.NOUI) {
      insertedVertex.setVisible(true);
    } else if (this.stickersSvc) {
      const {author, paperClass, tags} = data;
      const {id} = this.authService.getUserDataSync();
      const visible = this.stickersSvc.isNoteVisible(author?.id || id, paperClass, tags);

      insertedVertex.setVisible(visible);
    }

    return insertedVertex;
  }

  public createSwimlane(data, parent?) {
    const graph = this.editorRegistrySvc.getGraph();
    const styles = this.stylesSvc.swimlane(data);

    const insertedVertex = graph.createVertex(
      parent || this.graphHelper.swimlaneParent(),
      data.mxKey,
      this.encodeHtml.transform(data.text, true),
      0,
      0,
      data.size.w,
      data.size.h,
      styles.mxCellCss()
    );

    insertedVertex.setConnectable(false);

    return insertedVertex;
  }
}
